import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAuth } from '../contexts/AuthContext'
import { activateForm, createOneTimeCheckout } from '../services/paymentApi'
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate'

interface ActivationGateProps {
  formId: string
  onActivated: () => void
}

export default function ActivationGate({ formId, onActivated }: ActivationGateProps) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { user, credits, refresh } = useAuth()
  const [activating, setActivating] = useState(false)
  const [error, setError] = useState('')

  const handleUseCredit = async () => {
    setActivating(true)
    setError('')
    try {
      await activateForm(formId)
      await refresh()
      onActivated()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.activationError'))
    } finally {
      setActivating(false)
    }
  }

  const handlePayOnce = async () => {
    setActivating(true)
    setError('')
    try {
      const { checkoutUrl } = await createOneTimeCheckout(formId)
      window.location.href = checkoutUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.checkoutError'))
      setActivating(false)
    }
  }

  return (
    <div className="activation-gate">
      <div className="activation-gate-icon">
        <FontAwesomeIcon icon="lock" />
      </div>
      <h3>{t('activation.title')}</h3>
      <p className="text-gray">{t('activation.description')}</p>

      {error && <p className="text-danger text-sm mb-2" role="alert">{error}</p>}

      <div className="activation-gate-actions">
        {/* Always show pay button — no login required */}
        <button
          className="btn btn-primary btn-lg"
          onClick={handlePayOnce}
          disabled={activating}
        >
          {activating ? (
            <FontAwesomeIcon icon="spinner" className="spinner" />
          ) : (
            <FontAwesomeIcon icon="credit-card" />
          )}
          {t('activation.payOnce')}
        </button>

        {/* Show credit button if user has credits */}
        {user && credits > 0 && (
          <button
            className="btn btn-success btn-lg"
            onClick={handleUseCredit}
            disabled={activating}
          >
            <FontAwesomeIcon icon="check" />
            {t('activation.useCredit', { count: credits })}
          </button>
        )}

        {/* Hint to sign in for credits/free doc */}
        {!user && (
          <>
          <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>— {t('home.or')} —</div>
          <button
            className="btn btn-success btn-lg"
            onClick={() => navigate(`/login?redirect=/dashboard/${formId}`)}
            style={{ flexDirection: 'column', lineHeight: 1.4 }}
          >
            <strong>{t('auth.signIn')}</strong>
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>{t('activation.loginForCredits')}</span>
          </button>
          </>
        )}

        <button
          className="btn btn-outline mt-2"
          onClick={() => navigate('/pricing')}
        >
          {t('activation.viewPlans')}
        </button>
      </div>
    </div>
  )
}
